import { useState, useEffect, useCallback } from 'react'
import { monumentAPI, classifyEra } from '../services/api'
import { useDebounce } from 'use-debounce'

export const useMonuments = () => {
  const [monuments, setMonuments] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch] = useDebounce(searchQuery, 500)
  const [filters, setFilters] = useState({
    era: '',
    source: '',
    sortBy: 'relevance'
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: true
  })

  // Search monuments
  const searchMonuments = useCallback(async (query, options = {}) => {
    setLoading(true)
    setError(null)
    
    try {
      const searchFilters = { ...filters, ...options.filters }
      const results = await monumentAPI.searchMonuments(query, searchFilters)
      
      // Classify eras for monuments that don't have them
      const monumentsWithEras = results.map(monument => ({
        ...monument,
        era: monument.era || classifyEra(monument.description, monument.title)
      }))
      
      // Apply era filter if specified
      let filteredResults = monumentsWithEras
      if (searchFilters.era && searchFilters.era !== 'all') {
        filteredResults = monumentsWithEras.filter(monument => 
          monument.era === searchFilters.era
        )
      }
      
      // Apply source filter if specified
      if (searchFilters.source && searchFilters.source !== 'all') {
        filteredResults = filteredResults.filter(monument => 
          monument.source === searchFilters.source
        )
      }
      
      // Sort results
      const sortedResults = sortMonuments(filteredResults, searchFilters.sortBy)
      
      setMonuments(sortedResults)
      setPagination(prev => ({
        ...prev,
        total: sortedResults.length,
        hasMore: sortedResults.length > prev.limit
      }))
      
      return sortedResults
    } catch (err) {
      setError(err.message || 'Failed to search monuments')
      setMonuments([])
      return []
    } finally {
      setLoading(false)
    }
  }, [filters])

  // Get monuments in map bounds
  const getMonumentsInBounds = useCallback(async (bounds) => {
    setLoading(true)
    setError(null)
    
    try {
      const results = await monumentAPI.getMonumentsInBounds(bounds)
      
      const monumentsWithEras = results.map(monument => ({
        ...monument,
        era: monument.era || classifyEra(monument.description, monument.title)
      }))
      
      setMonuments(monumentsWithEras)
      return monumentsWithEras
    } catch (err) {
      setError(err.message || 'Failed to load monuments in area')
      setMonuments([])
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  // Get nearby monuments
  const getNearbyMonuments = useCallback(async (lat, lon, radius = 10000) => {
    setLoading(true)
    setError(null)
    
    try {
      const results = await monumentAPI.getNearbyMonuments(lat, lon, radius)
      
      const monumentsWithEras = results.map(monument => ({
        ...monument,
        era: monument.era || classifyEra(monument.description, monument.title)
      }))
      
      setMonuments(monumentsWithEras)
      return monumentsWithEras
    } catch (err) {
      setError(err.message || 'Failed to load nearby monuments')
      setMonuments([])
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  // Get monument details
  const getMonumentDetails = useCallback(async (id, source = 'wikipedia') => {
    try {
      const details = await monumentAPI.getMonumentDetails(id, source)
      if (details) {
        return {
          ...details,
          era: details.era || classifyEra(details.description, details.title)
        }
      }
      return null
    } catch (err) {
      setError(err.message || 'Failed to load monument details')
      return null
    }
  }, [])

  // Sort monuments helper
  const sortMonuments = (monuments, sortBy) => {
    const sorted = [...monuments]
    
    switch (sortBy) {
      case 'title':
        return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''))
      case 'era':
        return sorted.sort((a, b) => (a.era || '').localeCompare(b.era || ''))
      case 'source':
        return sorted.sort((a, b) => (a.source || '').localeCompare(b.source || ''))
      case 'relevance':
      default:
        return sorted
    }
  }

  // Auto-search when debounced query changes
  useEffect(() => {
    if (debouncedSearch.trim()) {
      searchMonuments(debouncedSearch)
    } else {
      setMonuments([])
      setPagination(prev => ({ ...prev, total: 0, hasMore: false }))
    }
  }, [debouncedSearch, searchMonuments])

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchQuery('')
    setMonuments([])
    setError(null)
  }, [])

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }, [])

  // Load more monuments
  const loadMore = useCallback(() => {
    if (!pagination.hasMore || loading) return
    
    setPagination(prev => ({
      ...prev,
      page: prev.page + 1
    }))
    
    // In a real implementation, you'd fetch the next page
    // For now, we'll just show more of the existing results
    const nextLimit = pagination.page * pagination.limit + pagination.limit
    setMonuments(prev => prev.slice(0, nextLimit))
  }, [pagination.hasMore, loading, pagination.page, pagination.limit])

  return {
    monuments,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    updateFilters,
    pagination,
    searchMonuments,
    getMonumentsInBounds,
    getNearbyMonuments,
    getMonumentDetails,
    clearSearch,
    loadMore
  }
}
